import * as React from 'react';
import { useState } from 'react';

import { Button, Input } from '@components/common';
import { Tooltip } from '@components/Tooltip';
import { uid, without } from '@components/utils';
import { usePermission } from '@hooks/usePermission';

type Attrs = CustomerImport['attrs'];

type FormValue = { attr: string; value: string; _id: number };

const toFormValues = (attrs: Attrs): FormValue[] =>
  Object.keys(attrs).map((attr) => ({ attr, value: attrs[attr], _id: uid() }));

const toAttrs = (values: FormValue[]): Attrs | null => {
  // Filter out attributes with empty names
  const validValues = values.filter((v) => v.attr.trim() !== '');

  return validValues.length === 0
    ? null
    : validValues.reduce((acc, cur) => {
        acc[cur.attr] = cur.value;
        return acc;
      }, {} as Attrs);
};

const focusAndScrollToId = (id: string) => {
  if (typeof document !== 'undefined') {
    const element = document.getElementById(id) as HTMLInputElement;
    if (element) {
      element.focus();
      element.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
    }
  }
};

interface Props {
  disabled?: boolean;
  value: Attrs | null;
  onChange: (value: Attrs) => void;
}
export const AdditionalAttrsForm: React.FC<React.PropsWithChildren<Props>> = ({ disabled, value, onChange }) => {
  const [values, setValues] = useState<FormValue[]>(value ? toFormValues(value) : []);
  const canManageAttrs = usePermission('manageAttrs')();

  const handleClickAdd = () => {
    const _id = uid();
    setValues([...values, { _id, attr: '', value: '' }]);
    setImmediate(() => focusAndScrollToId(`attr-${_id}`));
  };

  const onChangeValue = (_id: number, data: Partial<FormValue>) => {
    const value = values.find((v) => v._id === _id);

    if (data.attr === '') {
      const newValues = without(values, value) as FormValue[];
      setValues(newValues);
      // Persist the change immediately when removing an attribute
      const valToAttr = toAttrs(newValues);
      onChange(valToAttr || {});
    } else {
      const newValue = { ...value, ...data };
      const newValues = values?.map((v) => (v._id === _id ? newValue : v)) as FormValue[];
      setValues(newValues);
      // Persist the change immediately for any attribute or value change
      const valToAttr = toAttrs(newValues);
      valToAttr && onChange(valToAttr);
    }
  };

  return (
    <div className='space-y-4 pb-4'>
      {values.map(({ attr, value, _id }, i) => (
        <div key={_id} className='flex space-x-2'>
          <div className='flex-1'>
            <Input
              id={`attr-${_id}`}
              name={`attrs[${i}][name]`}
              ariaLabel={`attrs[${i}][name]`}
              disabled={disabled}
              onChange={(attr) => onChangeValue(_id, { attr })}
              className='w-full'
              placeholder='Attribute'
              value={attr}
            />
          </div>
          <div className='flex-1'>
            <Input
              name={`attrs[${i}][value]`}
              ariaLabel={`attrs[${i}][value]`}
              disabled={disabled}
              className='w-full'
              onChange={(value) => onChangeValue(_id, { value })}
              value={value}
              placeholder='Value'
            />
          </div>
        </div>
      ))}
      <div className=''>
        <Tooltip
          isDisabled={canManageAttrs}
          content='You do not have permission to create custom attributes. Contact your workspace admin.'
        >
          <Button
            aria-label='Add attribute'
            icon='plus'
            onClick={handleClickAdd}
            disabled={disabled || !canManageAttrs}
          >
            Add attribute
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};
